import type { ColumnDataType, ColumnDefinition, ColumnRole } from "../../schema/chart.js";

export function columnNames(cols: ColumnDefinition[]): string[] {
    return cols.map((c) => c.name);
}

export function getPrimaryKeyColumns(cols: ColumnDefinition[]): ColumnDefinition[] {
    const pks = cols.filter((c) => c.role === "primary_key");
    return pks.length > 0 ? pks : cols.length > 0 ? [cols[0]] : [];
}

export function getDataColumns(cols: ColumnDefinition[]): ColumnDefinition[] {
    return cols.filter((c) => c.role === "data");
}

export function getDisplayColumns(cols: ColumnDefinition[]): ColumnDefinition[] {
    return cols.filter((c) => c.role === "display");
}

const URL_RE = /^https?:\/\/.+/i;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const HEX_COLOR_RE = /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}/;
const BOOLEAN_STRINGS = new Set(["true", "false", "yes", "no", "t", "f", "y", "n", "1", "0"]);

function allMatch(values: string[], test: (v: string) => boolean): boolean {
    const nonEmpty = values.filter((v) => v !== "");
    if (nonEmpty.length === 0) return false;
    return nonEmpty.every(test);
}

export function detectDataType(values: string[]): ColumnDataType {
    if (allMatch(values, (v) => URL_RE.test(v))) return "url";
    if (allMatch(values, (v) => EMAIL_RE.test(v))) return "email";
    if (allMatch(values, (v) => HEX_COLOR_RE.test(v))) return "color";
    if (allMatch(values, (v) => ISO_DATE_RE.test(v) && !isNaN(Date.parse(v)))) return "date_iso";
    if (allMatch(values, (v) => BOOLEAN_STRINGS.has(v.toLowerCase()))) return "boolean";

    if (allMatch(values, (v) => !isNaN(Number(v)))) {
        const nums = values.filter((v) => v !== "").map(Number);
        // Epoch: all values > year 1970 in seconds (roughly 86400) and plausible timestamps
        if (nums.length > 0 && nums.every((n) => n > 86400 && n < 1e13)) return "date_epoch";
        // Rating: all integers 1-5
        if (nums.length > 0 && nums.every((n) => Number.isInteger(n) && n >= 1 && n <= 5))
            return "rating";
        return "number";
    }

    return "string";
}

function defaultRoleForType(dataType: ColumnDataType): ColumnRole {
    switch (dataType) {
        case "url":
        case "email":
        case "image_url":
        case "color":
            return "display";
        case "number":
        case "percentage":
        case "currency":
        case "duration":
        case "rating":
        case "date_epoch":
            return "data";
        case "string":
        case "boolean":
        case "date_iso":
            return "primary_key";
        default:
            return "display";
    }
}

export function detectColumns(
    names: string[],
    rawRows: Record<string, string>[],
): ColumnDefinition[] {
    let foundPrimaryKey = false;

    return names.map((name) => {
        const values = rawRows.map((row) => row[name] ?? "");
        const dataType = detectDataType(values);
        let role = defaultRoleForType(dataType);

        // Only the first primary_key-eligible column gets that role; rest become display
        if (role === "primary_key") {
            if (foundPrimaryKey) {
                role = "display";
            } else {
                foundPrimaryKey = true;
            }
        }

        return { name, role, dataType };
    });
}
